import { istDateKey } from '@/lib/ist'
import { formatPaise } from '@/lib/money'
import { cn } from '@/lib/cn'
import { bestBeforeLine, netQuantityLine, sampleLotCode } from '../../model/pack/labelForm'

export interface LabelPreviewData {
  productName: string
  netG: number | null
  mrpPaise: number | null
  shelfLifeDays: number | null
  fssaiLicenceNo: string | null
  allergenStatement: string | null
  claimText: string | null
  hallName: string | null
}

/**
 * What the printed pouch label carries: name, net quantity, MRP, best-before rule, FSSAI licence,
 * allergen line, claim, packed-at hall, the lot code's shape and where the QR goes. Missing lines
 * show as gaps to fill, in the warning colour.
 */
export function LabelPreview({ data, className }: { data: LabelPreviewData; className?: string }) {
  const missing = (text: string) => <span className="italic text-warn">{text}</span>
  return (
    <figure
      aria-label="How the label prints"
      className={cn('mx-auto w-full max-w-[380px] rounded-[6px] border-2 border-ink bg-white p-4 text-[#111] shadow-sm', className)}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[19px] font-bold leading-tight">{data.productName || missing('Product name')}</div>
          <div className="mt-1 text-[14px]">
            Net quantity: <b>{data.netG ? netQuantityLine(data.netG) : missing('not set')}</b>
          </div>
          <div className="text-[14px]">
            MRP: <b>{data.mrpPaise !== null ? formatPaise(data.mrpPaise) : missing('not set')}</b>{' '}
            <span className="text-[11px]">(incl. of all taxes)</span>
          </div>
        </div>
        <div
          aria-hidden="true"
          className="flex h-[84px] w-[84px] shrink-0 items-center justify-center rounded-[3px] border-2 border-dashed border-[#555] font-mono text-[18px] font-bold text-[#555]"
        >
          QR
        </div>
      </div>

      <div className="mt-3 rounded-[3px] border border-[#999] px-2 py-1.5 text-[13px] leading-snug">
        {data.claimText ? <span className="font-semibold">{data.claimText}</span> : missing('Claim line')}
      </div>

      <div className="mt-2 text-[12.5px] leading-snug">
        <b>Allergen information:</b> {data.allergenStatement || missing('allergen line missing')}
      </div>

      <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 font-mono text-[12px]">
        <dt>LOT</dt>
        <dd>{sampleLotCode(istDateKey())}</dd>
        <dt>PKD</dt>
        <dd>pack date</dd>
        <dt>BB</dt>
        <dd className="font-sans">{bestBeforeLine(data.shelfLifeDays)}</dd>
      </dl>

      <div className="mt-3 border-t border-[#999] pt-2 text-[11.5px] leading-snug">
        <div>
          Packed at: {data.hallName || missing('packing hall')}
        </div>
        <div>
          FSSAI Lic. No. {data.fssaiLicenceNo ? <span className="font-mono">{data.fssaiLicenceNo}</span> : missing('the hall has no licence on file')}
        </div>
      </div>
    </figure>
  )
}
